import type { AgentDashboardPayload } from "@noverik/contracts";
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Clock3,
  FilePlus2,
  Headphones,
  Inbox,
  ShieldCheck,
  UserCheck,
  UsersRound,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiRequest } from "../lib/api";

type Props = { mode?: "client" | "agent" };

export function DashboardPage({ mode = "client" }: Props) {
  const isAgent = mode === "agent";
  const [dashboard, setDashboard] = useState<AgentDashboardPayload | null>(null);

  useEffect(() => {
    if (!isAgent) return;

    let active = true;
    apiRequest<AgentDashboardPayload>("/agent/dashboard")
      .then((payload) => {
        if (active) setDashboard(payload);
      })
      .catch(() => {
        if (active) setDashboard(null);
      });

    return () => {
      active = false;
    };
  }, [isAgent]);

  const cards = isAgent
    ? [
        { to: "/desk/novo", title: "Novo chamado", text: "Registre uma solicitação em nome do cliente.", icon: FilePlus2, tone: "blue" },
        { to: "/desk/chamados", title: "Fila de chamados", text: "Acompanhe prioridades e atendimentos.", icon: Inbox, tone: "green" },
        { to: "/desk/clientes", title: "Clientes", text: "Consulte pessoas e organizações atendidas.", icon: UsersRound, tone: "purple" },
        { to: "/desk/base", title: "Base de conhecimento", text: "Consulte respostas e procedimentos.", icon: BookOpen, tone: "amber" },
      ]
    : [
        { to: "/novo", title: "Novo chamado", text: "Abra uma nova solicitação de suporte.", icon: FilePlus2, tone: "blue" },
        { to: "/chamados", title: "Meus chamados", text: "Acompanhe respostas e histórico.", icon: Inbox, tone: "green" },
        { to: "/chamados", title: "Consultar chamado", text: "Localize rapidamente um atendimento.", icon: Clock3, tone: "purple" },
        { to: "/chamados", title: "Histórico", text: "Consulte seus atendimentos anteriores.", icon: BookOpen, tone: "amber" },
      ];

  return (
    <div className="page">
      <section className="hero">
        <div>
          <span className="eyebrow">CENTRAL DE ATENDIMENTO</span>
          <h1>{isAgent ? "Sua operação de suporte, organizada." : "Tecnologia que mantém sua empresa em movimento."}</h1>
          <p>
            {isAgent
              ? "Acompanhe filas, prioridades, clientes e atendimentos em um único lugar."
              : "Abra chamados, acompanhe o andamento e mantenha todo o histórico de suporte centralizado."}
          </p>
          <div className="hero-actions">
            <Link className="button primary" to={isAgent ? "/desk/chamados" : "/novo"}>
              {isAgent ? "Ver fila de chamados" : "Solicitar suporte"}
              <ArrowRight size={17} />
            </Link>
            <Link className="button secondary" to={isAgent ? "/desk/clientes" : "/chamados"}>
              {isAgent ? "Ver clientes" : "Meus chamados"}
            </Link>
          </div>
        </div>

        <div className="hero-visual" aria-hidden="true">
          <div className="orb orb-a" />
          <div className="orb orb-b" />
          <div className="device">
            <div className="device-screen">
              <img className="device-screen-logo" src="/brand/noverik-logo-mono.png" alt="Noverik Tecnologia" />
            </div>
            <div className="device-base" />
          </div>
          <span className="float-icon one"><Headphones size={21} /></span>
          <span className="float-icon two"><ShieldCheck size={21} /></span>
        </div>
      </section>

      {isAgent && dashboard && (
        <>
          <section className="ops-stats">
            <Link to="/desk/chamados?state=open" className="ops-stat">
              <span className="meta-icon blue"><Inbox size={19} /></span>
              <div><strong>{dashboard.counts.open}</strong><span>Abertos</span></div>
            </Link>
            <Link to="/desk/chamados?status=Pendente%20cliente" className="ops-stat">
              <span className="meta-icon amber"><Clock3 size={19} /></span>
              <div><strong>{dashboard.counts.pendingClient}</strong><span>Pendente cliente</span></div>
            </Link>
            <Link to="/desk/chamados" className="ops-stat">
              <span className="meta-icon green"><UserCheck size={19} /></span>
              <div><strong>{dashboard.counts.mine}</strong><span>Comigo</span></div>
            </Link>
            <Link to="/desk/chamados" className="ops-stat">
              <span className="meta-icon purple"><UsersRound size={19} /></span>
              <div><strong>{dashboard.counts.unassigned}</strong><span>Sem responsável</span></div>
            </Link>
            <Link to="/desk/chamados" className="ops-stat danger">
              <span className="meta-icon amber"><AlertTriangle size={19} /></span>
              <div><strong>{dashboard.counts.overdue}</strong><span>Atrasados</span></div>
            </Link>
            <Link to="/desk/chamados?state=closed" className="ops-stat">
              <span className="meta-icon green"><CheckCircle2 size={19} /></span>
              <div><strong>{dashboard.counts.closed}</strong><span>Fechados</span></div>
            </Link>
          </section>

          <section className="dashboard-recent">
            <div className="section-heading">
              <div>
                <span className="eyebrow">EM ANDAMENTO</span>
                <h2>Chamados recentes</h2>
              </div>
              <Link className="button secondary" to="/desk/chamados">Ver todos</Link>
            </div>

            <div className="ticket-table-wrap">
              <table className="ticket-table">
                <thead>
                  <tr>
                    <th>Chamado</th>
                    <th>Assunto</th>
                    <th>Solicitante</th>
                    <th>Status</th>
                    <th>Departamento</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.recent.map((ticket) => (
                    <tr key={ticket.id}>
                      <td><Link className="ticket-number" to={"/desk/chamados/" + ticket.number}>#{ticket.number}</Link></td>
                      <td><Link className="ticket-subject" to={"/desk/chamados/" + ticket.number}>{ticket.subject || "Sem assunto"}</Link></td>
                      <td>{ticket.requester.name || ticket.requester.email}</td>
                      <td><span className={"status status-" + ticket.status.state}>{ticket.status.label}</span>
                        {ticket.overdue &&
                          <span className="status status-overdue">Atrasado</span>}</td>
                      <td>{ticket.department || "—"}</td>
                    </tr>
                  ))}
                  {dashboard.recent.length === 0 && (
                    <tr><td colSpan={5}><div className="table-empty"><strong>Nenhum chamado aberto.</strong></div></td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      <section className="quick-grid">
        {cards.map(({ to, title, text, icon: Icon, tone }) => (
          <Link key={title} to={to} className="quick-card">
            <span className={"card-icon " + tone}><Icon size={22} /></span>
            <div>
              <strong>{title}</strong>
              <p>{text}</p>
            </div>
            <ArrowRight className="card-arrow" size={18} />
          </Link>
        ))}
      </section>

      <section className="service-strip">
        <div><span className="status-dot" /><strong>Atendimento organizado</strong><small>Histórico completo e centralizado.</small></div>
        <div><ShieldCheck size={18} /><strong>Suporte especializado</strong><small>Infraestrutura, sistemas e serviços.</small></div>
        <div><Headphones size={18} /><strong>Comunicação direta</strong><small>Respostas no próprio chamado.</small></div>
      </section>
    </div>
  );
}
